import { useMemo, useState } from 'react'
import { CalendarDays, ChevronLeft, ChevronRight, ExternalLink, Info, List, MapPin, Rows3 } from 'lucide-react'
import { api } from '../lib/apiClient'
import { classNames, formatTime } from '../lib/formatters'
import type { CalendarEvent } from '../types'
import { useApi } from '../hooks/useApi'
import { EmptyState, ErrorState, LoadingState } from '../components/States'
import { PageHeader } from '../components/PageHeader'

function startOfWeek(date: Date) {
  const copy = new Date(date)
  const day = copy.getDay()
  copy.setDate(copy.getDate() - (day === 0 ? 6 : day - 1))
  copy.setHours(0, 0, 0, 0)
  return copy
}

function addDays(date: Date, amount: number) {
  const copy = new Date(date)
  copy.setDate(copy.getDate() + amount)
  return copy
}

function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

export function CalendarPage() {
  const [week, setWeek] = useState(() => startOfWeek(new Date()))
  const [view, setView] = useState<'week' | 'agenda'>('week')
  const end = addDays(week, 7)
  const { data, loading, error, reload } = useApi(
    () => api.calendar<CalendarEvent>(week.toISOString(), end.toISOString()),
    [week.getTime()],
  )
  const days = useMemo(() => Array.from({ length: 7 }, (_, index) => addDays(week, index)), [week])
  const events = data ?? []
  const rangeLabel = week.getMonth() === addDays(week, 6).getMonth()
    ? new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' }).format(week)
    : `${new Intl.DateTimeFormat('en-GB', { month: 'short' }).format(week)} – ${new Intl.DateTimeFormat('en-GB', { month: 'short', year: 'numeric' }).format(addDays(week, 6))}`

  return (
    <>
      <PageHeader
        eyebrow="Schedule"
        title="Calendar"
        description="A read-only view of the dedicated TermoVista Google Calendar."
        actions={
          <div className="flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
            <button onClick={() => setView('week')} className={classNames('flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-bold', view === 'week' ? 'bg-pine-50 text-pine-700' : 'text-slate-500 hover:text-slate-800')}><Rows3 className="h-3.5 w-3.5" />Week</button>
            <button onClick={() => setView('agenda')} className={classNames('flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-bold', view === 'agenda' ? 'bg-pine-50 text-pine-700' : 'text-slate-500 hover:text-slate-800')}><List className="h-3.5 w-3.5" />Agenda</button>
          </div>
        }
      />

      <div className="mb-5 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div className="flex items-center gap-2">
          <button className="btn-secondary px-3 py-2" onClick={() => setWeek(startOfWeek(new Date()))}>Today</button>
          <div className="flex overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <button className="p-2.5 text-slate-500 hover:bg-slate-50 hover:text-slate-800" onClick={() => setWeek(addDays(week, -7))} aria-label="Previous week"><ChevronLeft className="h-4 w-4" /></button>
            <button className="border-l border-slate-200 p-2.5 text-slate-500 hover:bg-slate-50 hover:text-slate-800" onClick={() => setWeek(addDays(week, 7))} aria-label="Next week"><ChevronRight className="h-4 w-4" /></button>
          </div>
          <h2 className="ml-1 text-lg font-bold text-slate-900">{rangeLabel}</h2>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500"><Info className="h-3.5 w-3.5 text-pine-600" />Events are managed in Google Calendar</div>
      </div>

      <section className="card overflow-hidden">
        {loading ? <LoadingState label="Loading calendar events…" /> : error ? <ErrorState message={error} onRetry={reload} /> : view === 'week' ? (
          <div className="overflow-x-auto">
            <div className="grid min-h-[580px] min-w-[980px] grid-cols-7 divide-x divide-slate-100">
              {days.map((day) => {
                const dayEvents = events.filter((event) => sameDay(new Date(event.start), day))
                const today = sameDay(day, new Date())
                return (
                  <div key={day.toISOString()} className="bg-white">
                    <div className={classNames('border-b border-slate-100 px-3 py-3 text-center', today && 'bg-pine-50/70')}>
                      <div className={classNames('text-[10px] font-bold uppercase tracking-[0.12em]', today ? 'text-pine-700' : 'text-slate-400')}>{new Intl.DateTimeFormat('en-GB', { weekday: 'short' }).format(day)}</div>
                      <div className={classNames('mx-auto mt-1 flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold', today ? 'bg-pine-700 text-white' : 'text-slate-800')}>{day.getDate()}</div>
                    </div>
                    <div className="space-y-2 p-2.5">
                      {dayEvents.map((event, index) => (
                        <a
                          key={event.id}
                          href={event.htmlLink || undefined}
                          target={event.htmlLink ? '_blank' : undefined}
                          rel="noreferrer"
                          className={classNames('block rounded-lg border-l-[3px] p-2.5 transition hover:shadow-sm', index % 3 === 0 ? 'border-l-pine-600 bg-pine-50' : index % 3 === 1 ? 'border-l-ember-500 bg-ember-50' : 'border-l-violet-500 bg-violet-50')}
                        >
                          <div className="text-[10px] font-bold text-slate-500">{event.allDay ? 'All day' : `${formatTime(event.start)} – ${formatTime(event.end)}`}</div>
                          <div className="mt-1 line-clamp-2 text-xs font-bold leading-4 text-slate-800">{event.title}</div>
                          {event.location && <div className="mt-1.5 flex items-start gap-1 text-[10px] leading-4 text-slate-500"><MapPin className="mt-0.5 h-3 w-3 shrink-0" /><span className="line-clamp-1">{event.location}</span></div>}
                        </a>
                      ))}
                      {!dayEvents.length && <div className="pt-5 text-center text-[11px] text-slate-300">No events</div>}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ) : events.length ? (
          <div className="divide-y divide-slate-100">
            {events.map((event) => {
              const start = new Date(event.start)
              return (
                <div key={event.id} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-start">
                  <div className="flex w-24 shrink-0 items-center gap-3 sm:block">
                    <div className="text-xs font-bold uppercase tracking-wide text-pine-700">{new Intl.DateTimeFormat('en-GB', { weekday: 'short' }).format(start)}</div>
                    <div className="mt-0.5 text-sm font-bold text-slate-900">{new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(start)}</div>
                  </div>
                  <div className="flex min-w-0 flex-1 gap-3">
                    <div className="mt-1 h-9 w-1 shrink-0 rounded-full bg-pine-500" />
                    <div className="min-w-0"><div className="font-bold text-slate-900">{event.title}</div><div className="mt-1 text-xs font-semibold text-slate-500">{event.allDay ? 'All day' : `${formatTime(event.start)} – ${formatTime(event.end)}`}</div>{event.location && <div className="mt-2 flex items-center gap-1.5 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" />{event.location}</div>}{event.description && <p className="mt-2 line-clamp-2 text-sm leading-5 text-slate-500">{event.description}</p>}</div>
                  </div>
                  {event.htmlLink && <a href={event.htmlLink} target="_blank" rel="noreferrer" className="btn-ghost shrink-0">Open <ExternalLink className="h-3.5 w-3.5" /></a>}
                </div>
              )
            })}
          </div>
        ) : (
          <EmptyState icon={CalendarDays} title="Nothing scheduled this week" description="Events created in the configured Google Calendar will appear here automatically." />
        )}
      </section>
    </>
  )
}
